import * as Schema from "effect/Schema";
import { SystemSshTarget } from "./systemSshTarget.ts";

export const ComputerSshTarget = Schema.Struct({
  destination: SystemSshTarget.check(
    Schema.isPattern(/^(?:[A-Za-z0-9_][A-Za-z0-9_.-]*@)?[A-Za-z0-9_][A-Za-z0-9_.-]*$/),
  ),
  port: Schema.Int.check(Schema.isBetween({ minimum: 1, maximum: 65535 })),
});
export type ComputerSshTarget = typeof ComputerSshTarget.Type;
export const ComputerVerification = Schema.Struct({
  target: ComputerSshTarget,
  checkedAt: Schema.Number,
  platform: Schema.String,
  items: Schema.Array(
    Schema.Struct({
      name: Schema.String,
      state: Schema.Literals(["verified", "missing", "attention", "unknown"]),
      detail: Schema.String,
    }),
  ),
});
export type ComputerVerification = typeof ComputerVerification.Type;
export const ComputerSshCheck = Schema.Struct({
  platform: Schema.String,
  prerequisites: Schema.Array(Schema.String),
  verification: Schema.optionalKey(ComputerVerification),
});
export type ComputerSshCheck = typeof ComputerSshCheck.Type;
/** `<platform>-<architecture>`, as `environmentServicePlatformSlug` spells it. */
export const ComputerPlatform = Schema.String.check(Schema.isPattern(/^[a-z0-9]+-[a-z0-9]+$/));
export type ComputerPlatform = typeof ComputerPlatform.Type;
export const ComputerRelease = Schema.Struct({
  id: Schema.String,
  version: Schema.String,
  url: Schema.String,
  platform: ComputerPlatform,
  fileName: Schema.String,
  sha256: Schema.String,
});
export type ComputerRelease = typeof ComputerRelease.Type;
/**
 * What a release offers, before anyone knows which machine it is for. A release
 * publishes one executable per platform, and which one this computer needs is
 * not known until the SSH probe has said what the computer is.
 */
export const ComputerReleaseAvailability = Schema.Union([
  Schema.Struct({
    state: Schema.Literal("ready"),
    id: Schema.String,
    version: Schema.String,
    platforms: Schema.Array(ComputerPlatform),
    notice: Schema.optionalKey(Schema.String),
  }),
  Schema.Struct({ state: Schema.Literal("unavailable"), message: Schema.String }),
]);
export type ComputerReleaseAvailability = typeof ComputerReleaseAvailability.Type;
export const ComputerProductionRelease = Schema.Union([
  Schema.Struct({
    state: Schema.Literal("ready"),
    release: ComputerRelease,
    notice: Schema.optionalKey(Schema.String),
  }),
  Schema.Struct({ state: Schema.Literal("unavailable"), message: Schema.String }),
]);
export type ComputerProductionRelease = typeof ComputerProductionRelease.Type;
export const ComputerSetupRequest = Schema.Struct({
  mode: Schema.Literals(["development", "production"]),
  /**
   * Where the install runs, not how automatic it is — both do the whole job on their own.
   * `commands` is pasted on the computer itself; `ssh` is driven from here.
   */
  method: Schema.Literals(["commands", "ssh"]),
  ssh: Schema.optionalKey(ComputerSshTarget),
  /**
   * Which machine the executable is for. Stated only by the `commands` method,
   * which has no probe to ask; an SSH install learns it from the computer
   * itself rather than from whoever is filling in the form.
   */
  platform: Schema.optionalKey(ComputerPlatform),
});
export type ComputerSetupRequest = typeof ComputerSetupRequest.Type;
export const ComputerSetupStatus = Schema.Struct({
  releaseNotice: Schema.optionalKey(Schema.String),
  id: Schema.String,
  phase: Schema.Literals([
    "checking",
    "prerequisites",
    "building",
    "downloading",
    "transferring",
    "installing",
    "waiting",
    /**
     * The destination asked for a password and this device has none saved for it. Setup is
     * paused, not failed: it resumes when the person answers through `provideSshPassword`, and
     * fails only if they decline. Reporting this as an error is what made adding a
     * password-authenticated computer look broken rather than merely unanswered.
     */
    "password-required",
    "linked",
    "failed",
    "cancelled",
  ]),
  message: Schema.String,
  commands: Schema.optionalKey(Schema.String),
  environmentId: Schema.optionalKey(Schema.String),
  revocationPending: Schema.optionalKey(Schema.Boolean),
  /** Set while `phase` is `password-required`: what the destination is asking to unlock. */
  passwordPrompt: Schema.optionalKey(
    Schema.Struct({
      destination: Schema.String,
      prompt: Schema.String,
    }),
  ),
  verification: Schema.optionalKey(ComputerVerification),
});
export type ComputerSetupStatus = typeof ComputerSetupStatus.Type;
export interface ComputerSetupBridge {
  options(): Promise<{
    development: boolean;
    unavailableReason?: string;
    current?: ComputerSetupStatus;
  }>;
  productionRelease(): Promise<ComputerReleaseAvailability>;
  testSsh(target: ComputerSshTarget): Promise<ComputerSshCheck>;
  start(request: ComputerSetupRequest): Promise<ComputerSetupStatus>;
  status(id: string): Promise<ComputerSetupStatus>;
  cancel(id: string): Promise<void>;
  /**
   * Answers a `password-required` pause. A null password is the person declining, which ends the
   * setup rather than retrying with an empty secret.
   */
  provideSshPassword(id: string, password: string | null): Promise<ComputerSetupStatus>;
}
